import { useState, useEffect } from 'react';
import { BlurView } from 'expo-blur';
import { VStack, Input, IconButton } from 'native-base';
import { theme } from '@breakroom-nx/assets';
import { SearchIcon } from 'react-native-heroicons/solid';
import { UserListTableViewCell } from '../../friend-list/user-list-table-view-cell/user-list-table-view-cell';
import { UserList } from '../../user/user-list/user-list';

import * as Yup from 'yup';
import { Formik } from 'formik';
import { useLazyGetUsersForUsernameQuery } from '@breakroom-nx/redux';
import { SerializedError } from '@reduxjs/toolkit';

const usernameRegExp = /\w/;
const SearchScheme = Yup.object().shape({
  username: Yup.string()
    .matches(
      usernameRegExp,
      "Only letters, numbers and '_' underscores allowed"
    )
    .max(32, 'Too Long!'),
});

interface SearchFormValues {
  username: string;
}

/* eslint-disable-next-line */
export interface SearchBarProps {}

export function SearchBar(props: SearchBarProps) {
  const [focused, setFocused] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState(''); // Debounced value
  const initialValues: SearchFormValues = {
    username: '',
  };

  const [trigger, { data: users, isLoading, isSuccess, isError, error }] =
    useLazyGetUsersForUsernameQuery();

  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);

  const renderUsers = () => {
    if (filteredUsers && filteredUsers.length > 0) {
      return filteredUsers.map((user) => (
        <UserListTableViewCell
          uid={user.uid}
          username={user.username}
          key={user.uid}
        />
      ));
    }
    return null;
  };

  const handleFetchUser = (username: string) => {
    // console.log(`Searching for Username: ${username}`);
    trigger(username).then((result) => {
      if (result.data) {
        setFilteredUsers(result.data);
      }
    });
  };

  const handleClearUsers = () => {
    setFilteredUsers([]); // Clear the filtered users list
  };

  // Debounce Effect
  useEffect(() => {
    const handler = setTimeout(() => {
      if (debouncedValue) {
        handleFetchUser(debouncedValue);
      } else {
        handleClearUsers();
      }
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(handler); // Clear timeout on cleanup
    };
  }, [debouncedValue]);

  return (
    <BlurView intensity={25} tint={'dark'} style={{ borderRadius: 10 }}>
      <VStack>
        <Formik
          initialValues={initialValues}
          validationSchema={SearchScheme}
          validateOnChange={true}
          onSubmit={async (values, { setSubmitting }) => {
            handleFetchUser(values.username);
          }}
        >
          {({
            values,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <VStack
              space={2}
              px={{ lg: 3 }}
              py={{ lg: 1 }}
            >
              <Input
                onFocus={() => {
                  setFocused(true);
                }}
                onBlur={(e) => {
                  setFocused(false);
                  handleBlur('username')(e);
                }}
                onChangeText={(text) => {
                  handleChange('username')(text);
                  setDebouncedValue(text); // Update debounced value
                }}
                onSubmitEditing={() => handleSubmit()}
                value={values.username}
                flex={1}
                placeholder={'Search by username...'}
                color={focused ? 'white' : 'surface.500'}
                placeholderTextColor={
                  focused ? theme.colors.text[300] : theme.colors.text[500]
                }
                backgroundColor={'none'}
                height="full"
                marginRight={3}
                fontSize={16}
                fontWeight={400}
                letterSpacing={'-0.3px'}
                alignSelf={'stretch'}
                bg={'transparent'}
                borderTopColor={'transparent'}
                borderRightColor={'transparent'}
                borderLeftColor={'transparent'}
                focusOutlineColor={'transparent'}
                borderBottomWidth={0}
                borderLeftWidth={0}
                borderRightWidth={0}
                rounded={0}
                InputRightElement={
                  <IconButton
                    disabled={isSubmitting}
                    onPress={() => {
                      handleSubmit();
                    }}
                    borderRadius={'full'}
                    padding={1}
                    size={8}
                    icon={
                      <SearchIcon
                        color={theme.colors.primary[400]}
                        size={'20px'}
                      />
                    }
                    _pressed={{ backgroundColor: 'transparent', opacity: 0.7 }}
                  />
                }
              />
            </VStack>
          )}
        </Formik>
        {(filteredUsers.length > 0 || (isError && debouncedValue)) && (
          <UserList
            data={filteredUsers}
            isLoading={isLoading}
            isSuccess={isSuccess}
            isError={isError}
            error={isError ? (error as SerializedError) : undefined}
            refetch={() => {
              if (debouncedValue) handleFetchUser(debouncedValue);
            }}
            pb={0}
            px={0}
          />
        )}
      </VStack>
    </BlurView>
  );
}

export default SearchBar;
